import { useState, useRef, useEffect, useMemo } from "react";

import { Button } from "./ui-common/button";
import { Card, CardContent } from "./ui-common/card";
import { Badge } from "./ui-common/badge";
import { Input } from "./ui-common/input";
import { Textarea } from "./ui-common/textarea";
import { Popover, PopoverTrigger, PopoverContent } from "./ui-common/popover";
import { ScrollArea } from "./ui-common/scroll-area";
import { Switch } from "./ui-common/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui-common/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui-common/select";

import { 
  ArrowLeft, 
  ArrowUpDown,
  MessageSquare, 
  MessagesSquare, 
  Search, 
  Globe,
  Book,
  BookPlus,
  MessageCirclePlus,
  Send, 
  Sliders, 
  LifeBuoy, 
  BookText,
  Trash2,
  BookOpen,
  Copy,
  BotMessageSquare,
  Check,
  X,
  Plus,
  PanelLeftClose
} from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import axiosInstance, { apiBaseURL } from "./axiosInstance";
import Feedback from "./Feedback";
import LecturesMaterialsPanel from "./LecturesMaterialsPanel";
import {ChatResponse, SearchResponse} from "./ui-chat/response";
import { formatDate, getGreeting } from "../lib/utils";
import { SourceProvider } from "../contexts/SourceContext";

const mode_options = {
  default: {
    mode_name: 'default',  // mode_name is the key here; to be used to identify the mode (in a stateful variable)
    interaction_type: 'chat', // the interaction-type string from the message history
  
    text: 'Ask AI',

    Icon: BookPlus,
    description: 'Get the best answers on your course materials',
    input_placeholder_text: 'Ask your Study Buddy using course + broader knowledge...',
    api_route: "/api/chat",
    disabled: false,
    response_component: ChatResponse,
  },
  search: {
    mode_name: 'search',
    interaction_type: 'search',
    text: 'Search Materials',
    Icon: Search,
    description: 'Search within your course materials',
    input_placeholder_text: 'Study Buddy will search your course by meaning, not just keywords…',
    api_route: "/api/search",
    disabled: false,
    response_component: SearchResponse,
  },
  /*
  // Not implemented yet
  web: {
    mode_name: 'web',
    interaction_type: 'web',
    text: 'Web [?]',
    Icon: Globe,
    description: 'Search the web',
    input_placeholder_text: 'Search the web...',
    disabled: true,
    response_component: ChatResponse,
  }
  */
};


const ChatPage = ({ 
  user, 
  selectedCourse, 
  chatHistory,
  updateChatTitles,
  currentMessages,
  currentConversationId,
  onBackToCourses, 
  onLogout,
  onNewConversation,
  onSwitchConversation,
  onDeleteConversation,
  setMessages,
  updateMessageById,
  onNavigateToSupport
}) => {
  const courseLabel = selectedCourse?.label;
  const courseName = selectedCourse?.name;
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [modeOption, setModeOption] = useState('default');
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    const saved = localStorage.getItem('leftPanelWidth');
    const savedWidth = saved ? parseInt(saved, 10) : 260;
    const minWidth = 240;
    const maxWidth = window.innerWidth * 0.4;
    return Math.max(minWidth, Math.min(savedWidth, maxWidth));
  });
  const [rightPanelWidth, setRightPanelWidth] = useState(() => {
    // Load saved width from localStorage or use default
    const saved = localStorage.getItem('rightPanelWidth');
    const savedWidth = saved ? parseInt(saved, 10) : 320;
    const minWidth = window.innerWidth * 0.2; // Minimum 20% of screen width
    return Math.max(savedWidth, minWidth);
  });
  const [resizingPanel, setResizingPanel] = useState(null); // 'left' | 'right' | null
  const [input, setInput] = useState("");
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null); // Track which conversation is being deleted
  const [chatTitles, setChatTitles] = useState({}); // Store titles for conversations
  const [lastSentMessageId, setLastSentMessageId] = useState(null);
  // Left pane search and sort state
  const [conversationSearchQuery, setConversationSearchQuery] = useState("");
  const [conversationSortOrder, setConversationSortOrder] = useState("most_recent");
  const lastMessageRef = useRef(null);
  const textareaRef = useRef(null);
  // Track courses we've already auto-spawned an empty chat for (per session)
  const spawnedEmptyForCourseRef = useRef({});

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  useEffect(() => {
    updateChatTitles(chatTitles);
  }, [chatTitles]);

  useEffect(() => {
    const course = courseName;
    if (!course) return;
    if (spawnedEmptyForCourseRef.current[course]) return;

    const hasEmpty = chatHistory.some(
      (conv) => conv.conversation_course === course && (!conv.messages || conv.messages.length === 0)
    );

    if (!hasEmpty) {
      onNewConversation();
    }

    spawnedEmptyForCourseRef.current[course] = true;
  }, [courseName]);

  // Keep all non-empty conversations and always show at most one empty conversation for the selected course
  const isEmptyConv = (conv) => {
    const len = conv.conversation_id === currentConversationId
      ? (currentMessages?.length || 0)
      : (conv.messages?.length || 0);
    return len === 0;
  };

  // Helper function to highlight search terms in text
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => {
      if (part.toLowerCase() === searchTerm.toLowerCase()) {
        return (
          <mark key={index} className="bg-yellow-200 dark:bg-yellow-600 text-yellow-900 dark:text-yellow-100 px-1 rounded">
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  // Helper function to determine match type for a conversation
  const getMatchType = (conversation, searchTerm) => {
    if (!searchTerm) return null;
    
    const title = chatTitles[conversation.conversation_id] || conversation.conversation_course_label || conversation.conversation_course || '';
    const dateStr = formatDate(conversation.creation_date);
    
    // Check metadata matches first
    const titleMatch = title.toLowerCase().includes(searchTerm);
    const dateMatch = dateStr.toLowerCase().includes(searchTerm);
    const courseMatch = (conversation.conversation_course_label || conversation.conversation_course)?.toLowerCase().includes(searchTerm);
    
    if (titleMatch || dateMatch || courseMatch) {
      return 'metadata';
    }
    
    // Check message content
    const hasMessageMatch = conversation.messages?.some(message => {
      const query = message.query || '';
      const response = message.response || '';
      return (
        query.toLowerCase().includes(searchTerm) ||
        response.toLowerCase().includes(searchTerm)
      );
    });
    
    if (hasMessageMatch) {
      return 'messages';
    }
    
    // Check quotes/sources
    const hasQuoteMatch = conversation.messages?.some(message => 
      message.quotes?.some(quote => {
        const quoteText = quote.text || '';
        const linkText = quote.link_text || '';
        return (
          quoteText.toLowerCase().includes(searchTerm) ||
          linkText.toLowerCase().includes(searchTerm)
        );
      })
    );
    
    if (hasQuoteMatch) {
      return 'sources';
    }
    
    // Check current messages if applicable
    if (conversation.conversation_id === currentConversationId && currentMessages?.some(message => {
      const query = message.query || '';
      const response = message.response || '';
      return (
        query.toLowerCase().includes(searchTerm) ||
        response.toLowerCase().includes(searchTerm) ||
        message.quotes?.some(quote => {
          const quoteText = quote.text || '';
          const linkText = quote.link_text || '';
          return (
            quoteText.toLowerCase().includes(searchTerm) ||
            linkText.toLowerCase().includes(searchTerm)
          );
        })
      );
    })) {
      return 'current';
    }
    
    return null;
  };

  // Helper function to filter conversations by search query
  const filterConversations = (conversations) => {
    if (!conversationSearchQuery) return conversations;
    
    const searchTerm = conversationSearchQuery.toLowerCase();
    
    return conversations.filter(conversation => {
      return getMatchType(conversation, searchTerm) !== null;
    });
  };

  // Helper function to sort conversations
  const sortConversations = (conversations) => {
    if (!conversations || conversations.length === 0) return conversations;
    
    return [...conversations].sort((a, b) => {
      switch (conversationSortOrder) {
        case 'most_recent':
          return new Date(b.creation_date) - new Date(a.creation_date);
        case 'oldest_first':
          return new Date(a.creation_date) - new Date(b.creation_date);
        case 'alphabetical':
          const titleA = chatTitles[a.conversation_id] || a.conversation_course_label || a.conversation_course || '';
          const titleB = chatTitles[b.conversation_id] || b.conversation_course_label || b.conversation_course || '';
          return titleA.toLowerCase().localeCompare(titleB.toLowerCase());
        default:
          return 0;
      }
    });
  };

  const conversationsForSelectedCourse = useMemo(() => {
    const byCourse = chatHistory.filter(
      (conversation) => conversation.conversation_course === courseName
    );

    const empty = byCourse.filter(isEmptyConv);
    // Non-empty conversations 
    const nonEmpty = byCourse.filter((conv) => !isEmptyConv(conv));

    // Apply search filter to non-empty conversations only
    const filteredNonEmpty = filterConversations(nonEmpty);
    
    // Apply sorting to filtered conversations
    const sortedNonEmpty = sortConversations(filteredNonEmpty);

    // Always show a single empty conversation (if exists), regardless of which conversation is selected
    const shouldShowEmpty = empty.length > 0;

    return [
      ...(shouldShowEmpty ? empty.slice(0, 1) : []),
      ...sortedNonEmpty,
    ];
  }, [chatHistory, courseName, currentConversationId, currentMessages, conversationSearchQuery, conversationSortOrder, chatTitles]);


  useEffect(() => {
    const titles = {};
    const conversations = chatHistory.filter(
      (conversation) => conversation.conversation_course === courseName
    );

    for (const conversation of conversations) {
      if (chatTitles[conversation.conversation_id] === undefined) {
        if (conversation.title && conversation.title.trim() !== "") {
          titles[conversation.conversation_id] = conversation.title;
        } else {
          titles[conversation.conversation_id] = conversation.conversation_course_label || conversation.conversation_course;
        }
      }
    }

    if (Object.keys(titles).length > 0) {
      setChatTitles((prev) => ({ ...prev, ...titles }));
    }
  }, [chatHistory, courseName, chatTitles]);

  useEffect(() => {
    if (lastSentMessageId && chatTitles[currentConversationId] === courseLabel) {
      let isCancelled = false;

      async function getNewTitleForConversation(conversationId) {
        try {
          const response = await axiosInstance.post(`/api/conversations/${conversationId}/generate-title`);
          if (response.data.title) {
            return response.data.title;
          }
          // If no title was generated, use a fallback
          return courseLabel;
        } catch (error) {
          console.error("Error generating title:", error);
          return courseLabel;
        }
      }

      (async () => {
        const newTitle = await getNewTitleForConversation(currentConversationId);
        if (!isCancelled) {
          setChatTitles((prevTitles) => ({
            ...prevTitles,
            [currentConversationId]: newTitle,
          }));
        }
      })();

      return () => {
        isCancelled = true;
      };
    }
  }, [lastSentMessageId, currentConversationId, courseLabel, chatTitles[currentConversationId]]);

  const handleSetModeOption = (newModeName) => {
    // If the button's mode is already the active one...
    if (newModeName === modeOption) {
      setModeOption("default"); // ...reset the state to its default.
    } else {
      setModeOption(newModeName); // ...otherwise, set the new mode.
    }
  };

  const prevMessageCountRef = useRef(0);

  // Scroll to bottom only when a new message is added, not on quote/content updates
  useEffect(() => {
    const count = currentMessages.length;
    if (count > prevMessageCountRef.current && lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    prevMessageCountRef.current = count;
  }, [currentMessages]);

  // Handle panel resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizingPanel) return;
      
      if (resizingPanel === 'right') {
        const newWidth = window.innerWidth - e.clientX;
        const minWidth = window.innerWidth * 0.2; // Minimum 20% of screen width
        const maxWidth = window.innerWidth * 0.6; // Maximum 60% of screen width
        
        if (newWidth >= minWidth && newWidth <= maxWidth) {
          setRightPanelWidth(newWidth);
          localStorage.setItem('rightPanelWidth', newWidth.toString());
        }
      } else if (resizingPanel === 'left') {
        const newWidth = e.clientX;
        const minWidth = 240;
        const maxWidth = window.innerWidth * 0.4; // Maximum 40% of screen width
        
        if (newWidth >= minWidth && newWidth <= maxWidth) {
          setLeftPanelWidth(newWidth);
          localStorage.setItem('leftPanelWidth', newWidth.toString());
        }
      }
    };

    const handleMouseUp = () => {
      setResizingPanel(null);
    };

    if (resizingPanel) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.body.classList.add('resizing');
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.classList.remove('resizing');
    };
  }, [resizingPanel]);

  const handleResizeStartLeft = (e) => {
    e.preventDefault();
    setResizingPanel('left');
  };

  const handleResizeStartRight = (e) => {
    e.preventDefault();
    setResizingPanel('right');
  };

  const handleDoubleClickLeft = () => {
    const minWidth = 220;
    const maxWidth = window.innerWidth * 0.4;
    const resetWidth = Math.max(minWidth, Math.min(240, maxWidth));
    setLeftPanelWidth(resetWidth);
    localStorage.setItem('leftPanelWidth', resetWidth.toString());
  };

  const handleDoubleClickRight = () => {
    const minWidth = window.innerWidth * 0.2; // Minimum 20% of screen width
    const resetWidth = Math.max(320, minWidth);
    setRightPanelWidth(resetWidth);
    localStorage.setItem('rightPanelWidth', resetWidth.toString());
  };

  const handleSendMessage = async () => {
    if (!input.trim() || !currentConversationId) return;

    const newMessageId = uuidv4();
    const newMessage = {
      query_uuid: newMessageId,
      query: input.trim(),
      response: "",
      quotes: [],
      interaction_type: mode_options[modeOption].interaction_type,
      timestamp: Date.now() / 1000, // unix timestamp in seconds
    };

    // Add user message immediately
    setMessages([...currentMessages, newMessage]);
    setInput("");
    setIsWaitingForResponse(true);

    // Use streaming for chat endpoints, fallback to regular for search
    const useStreaming = mode_options[modeOption].api_route === "/api/chat";
    
    if (useStreaming) {
      try {
        const streamUrl = new URL("/api/chat/stream", apiBaseURL).toString();

        // Use fetch for SSE streaming (EventSource doesn't support POST)
        const response = await fetch(streamUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // Include cookies for authentication
          body: JSON.stringify({
            query_uuid: newMessageId,
            query: input.trim(),
            conversation_id: currentConversationId,
            conversation_course: selectedCourse?.name,
            interaction_type: mode_options[modeOption].interaction_type,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let currentResponse = "";
        let currentQuotes = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          
          // Process complete SSE events (separated by \n\n)
          const events = buffer.split("\n\n");
          buffer = events.pop() || ""; // Keep incomplete event in buffer

          for (const eventText of events) {
            if (!eventText.trim()) continue;

            let eventType = null;
            let eventData = null;
            const lines = eventText.split("\n");

            for (const line of lines) {
              const trimmed = line.trim();
              if (trimmed.startsWith("event:")) {
                eventType = trimmed.substring(6).trim();
              } else if (trimmed.startsWith("data:")) {
                try {
                  eventData = JSON.parse(trimmed.substring(5).trim());
                } catch (e) {
                  console.error("Failed to parse SSE data:", e, trimmed);
                }
              }
            }

            // Process the event
            if (eventType && eventData) {
              if (eventType === "answer_chunk") {
                currentResponse += eventData.text || "";
                updateMessageById(newMessageId, {
                  response: currentResponse,
                  quotes: currentQuotes,
                });
              } else if (eventType === "quote_chunk") {
                // Quote text chunks are accumulated, but we update quotes via quotes_update events
              } else if (eventType === "quotes_update") {
                currentQuotes = eventData.quotes || [];
                updateMessageById(newMessageId, {
                  response: currentResponse,
                  quotes: currentQuotes,
                });
              } else if (eventType === "done") {
                // Final update with complete data
                currentQuotes = eventData.quotes || [];
                updateMessageById(newMessageId, {
                  response: currentResponse,
                  quotes: currentQuotes,
                });
                setLastSentMessageId(newMessageId);
              } else if (eventType === "error") {
                const errorMsg = eventData.error || "Sorry, I failed to answer your question 🤒. Please try again 🤗";
                updateMessageById(newMessageId, {
                  response: errorMsg,
                  quotes: [],
                });
              }
            }
          }
        }
      } catch (error) {
        console.error("Error streaming message:", error);
        updateMessageById(newMessageId, {
          response: "Sorry, I failed to answer your question 🤒. Please try again 🤗",
          quotes: [],
        });
      } finally {
        setIsWaitingForResponse(false);
      }
    } else {
      // Fallback to regular non-streaming for search endpoint
      try {
        const response = await axiosInstance.post(
          mode_options[modeOption].api_route, {
          query_uuid: newMessageId,
          query: input.trim(),
          conversation_id: currentConversationId,
          conversation_course: selectedCourse?.name,
          interaction_type: mode_options[modeOption].interaction_type,
        });

        if (response.data.status === "success" || response.data.status === "error") {
          updateMessageById(newMessageId, {
            response: response.data.response,
            quotes: response.data.quotes || []
          });
          setLastSentMessageId(newMessageId);
        } else {
          updateMessageById(newMessageId, {
            response: "Sorry, I failed to answer your question 🤒. Please try again 🤗",
            quotes: []
          });
        }
      } catch (error) {
        console.error("Error sending message:", error);
        updateMessageById(newMessageId, {
          response: "Sorry, I failed to answer your question 🤒. Please try again 🤗",
          quotes: []
        });
      } finally {
        setIsWaitingForResponse(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };


  const handleCopyMessage = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // Could add a toast notification here if available
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const handleSendSelectedTextToChat = (selectedText) => {
    if (selectedText && selectedText.trim()) {
      setInput(selectedText.trim());
      // Focus the input field
      setTimeout(() => {
        const inputElement = document.querySelector('input[type="text"]');
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    }
  };


  const confirmDeleteConversation = async (conversationId) => {
    try {
      await onDeleteConversation(conversationId);
      setConversationToDelete(null);
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const lastMessageId = currentMessages.length > 0 ? currentMessages[currentMessages.length - 1].query_uuid : null;

  return (
    <TooltipProvider>
      <div className="h-screen bg-background flex flex-col overflow-hidden">
        {/* Header - Fixed at top */}
        <div className="bg-muted px-2 py-3 flex-shrink-0">
          <div className="flex items-center justify-between px-4">
            {/* Welcome text top-left */}
            <div className="flex-1 flex items-center gap-2">
              <img src="/study-buddy.svg" alt="Study Buddy" className="h-8 w-8" />
              <p className="text-muted-foreground text-sm">{`${getGreeting()}, ${user?.name || 'there'}!`}</p>
            </div>
            
            {/* Course name centered */}
            <div className="text-center relative py-1">
              <div className="flex items-center justify-center gap-2 relative">
                <h1 className="text-2xl font-bold">{selectedCourse?.label || 'Course Chat'}{selectedCourse?.id ? ` ${selectedCourse.id}` : ''}</h1>
              </div>
            </div>
            
            {/* Controls on the right */}
            <div className="flex gap-1 items-center flex-1 justify-end">
              <div className="flex gap-0.5 items-center">
                {/* <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon">
                      <BookText className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>User Manual</p>
                  </TooltipContent>
                </Tooltip> */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={onBackToCourses}
                >
                  <ArrowLeft className="h-4 w-4 scale-x-[-1]" />
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={onNavigateToSupport}
                    >
                      <LifeBuoy className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Support & Feedback</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div className="flex-1 px-2 pb-2 pt-0 overflow-hidden">
          <div className="h-full rounded-lg border flex">
            {/* Left Pane - Conversations */}
            {!leftPanelCollapsed ? (
              <div
                className="bg-muted flex flex-col relative"
                style={{ width: `${leftPanelWidth}px`, minWidth: 240, maxWidth: '40vw' }}
              >
                {/* Resize Handle */}
                <div
                  className="absolute right-0 top-0 w-1 h-full bg-transparent hover:bg-primary/30 cursor-col-resize transition-colors z-10 group"
                  onMouseDown={handleResizeStartLeft}
                  onDoubleClick={handleDoubleClickLeft}
                  title="Drag to resize panel, double-click to reset"
                >
                  <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-primary/0 group-hover:bg-primary/50 rounded-full transition-colors" />
                </div>
                <div className="pb-2 pt-2 px-3 border-b">
                  {/* Main flex container to align items vertically in the center */}
                  <div className="flex items-center justify-between">
                    {/* Container for the text elements */}
                    <div>
                      <h1 className="font-semibold text-lg">Conversations</h1>
                    </div>
                    {/* The button with new hover/active effects */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="bg"
                            onClick={() => {
                              const currentCourseConversations = chatHistory.filter(
                                (conv) => conv.conversation_course === courseName
                              );
                              const hasEmptyConversation = currentCourseConversations.some((conv) => isEmptyConv(conv));
                              if (!hasEmptyConversation) {
                                onNewConversation();
                              }
                            }}
                            className="ml-4 h-8 w-8 p-0 rounded-full 
                                      transition-all duration-200 ease-in-out 
                                      hover:bg-accent hover:scale-125 
                                      hover:shadow-md hover:shadow-purple-500/10
                                      active:scale-95 
                                      active:shadow-lg active:shadow-purple-500/50">
                            <MessageCirclePlus className="h-6 w-6" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>New chat</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    {/* Collapse Button */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setLeftPanelCollapsed(true)}
                            className="ml-2"
                          >
                            <PanelLeftClose className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Collapse</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  
                  {/* Search and Sort Controls */}
                  <div className="mt-3 space-y-4">
                    {/* Search Bar */}
                    <div className="relative flex-1 flex items-center gap-0 pl-3 pr-3 rounded-md border border-primary/30 ring-0 transition-all focus-within:ring-2 focus-within:ring-primary/40 focus-within:border-primary"> 
                      
                      {/* The Search Icon - stays inside the unified focus ring */}
                      <Search className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-1" />
                      
                      <Input
                        placeholder="Search Conversations by Keywords..."
                        value={conversationSearchQuery}
                        onChange={(e) => setConversationSearchQuery(e.target.value)}
                        className="border-0 ring-0 bg-transparent text-xs flex-1 min-w-0 -ml-2 outline-none focus:ring-0 focus:outline-none focus:border-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
                      />
                    </div>
                      </div>
                    </div>
                <ScrollArea className="flex-1">
                  <div className="p-2 space-y-1">
                    {conversationsForSelectedCourse.map((conversation) => (
                      <Card
                        key={conversation.conversation_id}
                        className={`cursor-pointer mb-2 group ${
                          currentConversationId === conversation.conversation_id 
                            ? 'ring-2 ring-primary bg-primary/15 border-primary shadow-md' 
                            : 'border-border hover:border-primary/30 hover:bg-primary/5 transition-all'
                        }`}
                        onClick={() => !conversationToDelete && onSwitchConversation(conversation.conversation_id)}
                      >
                        <div className="group relative px-3 py-2">
                          <div className="space-y-0" style={{ minHeight: '2.5rem' }}>
                            <div className="flex items-start justify-start">
                              <div className="flex-1 min-w-0" dir="ltr">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <h3 className="font-medium leading-tight mb-1 break-words cursor-pointer text-left" 
                                        style={{
                                          fontSize: (conversation.conversation_course_label || conversation.conversation_course).length > 20 ? '0.75rem' : 
                                                   (conversation.conversation_course_label || conversation.conversation_course).length > 15 ? '0.8125rem' : '0.875rem',
                                          lineHeight: (conversation.conversation_course_label || conversation.conversation_course).length > 20 ? '1rem' : '1.25rem'
                                        }}>
                                      {highlightSearchTerm(chatTitles[conversation.conversation_id], conversationSearchQuery)}
                                    </h3>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{chatTitles[conversation.conversation_id]}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                <div className="flex items-center justify-between gap-0">
                                  <p className="text-[0.70rem] text-muted-foreground break-words" 
                                     style={{
                                       overflow: 'hidden',
                                       display: '-webkit-box',
                                       WebkitLineClamp: 1,
                                       WebkitBoxOrient: 'vertical'
                                     }}>
                                    {formatDate(conversation.creation_date)}
                                    {conversation.messages?.length > 0 && ` • ${conversation.messages.length} messages`}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 ml-1 flex-shrink-0">
                                {conversationToDelete === conversation.conversation_id ? (
                                  <div className="flex items-center gap-1">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button 
                                          variant="ghost" 
                                          size="sm"
                                          className="h-6 w-6 p-0 text-green-500 hover:text-green-600"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            confirmDeleteConversation(conversation.conversation_id);
                                          }}
                                        >
                                          <Check className="h-3 w-3" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Confirm delete</p>
                                      </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button 
                                          variant="ghost" 
                                          size="sm"
                                          className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setConversationToDelete(null);
                                          }}
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Cancel</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                ) : (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        className="h-6 w-6 p-0 group-hover:opacity-100 hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive opacity-5"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setConversationToDelete(conversation.conversation_id);
                                        }}
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Delete</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            ) : (
              // Collapsed Left Panel
              <div className="w-32 bg-muted flex flex-col items-center p-4">
                <div className="flex flex-col items-center gap-2">
                  {/* Expand Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setLeftPanelCollapsed(false)}
                    className="w-full flex flex-col items-center gap-1 h-auto py-3"
                  >
                    <MessagesSquare className="h-4 w-4" />
                    <span className="text-xs">Conversations</span>
                  </Button>
                  
                  {/* New Chat Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setLeftPanelCollapsed(false);
                      onNewConversation();
                    }}
                    className="w-full flex flex-col items-center gap-1 h-auto py-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="text-xs">New</span>
                  </Button>
                </div>
              </div>
            )}

            {/* Center Pane - Chat */}
            <div className="flex-1 flex flex-col bg-muted">
              {/* Chat header */}
              <div className="p-2 mb-2">
                <div className="flex items-center justify-end">

                </div>
              </div>

              {/* Chat messages area */}
              <ScrollArea className="flex-1 mb-4 rounded-md">
                <div className="max-w-5xl mx-auto p-3 space-y-2 w-full min-h-full flex flex-col justify-end">
                  {currentMessages.length === 0 ? (
                    <div className="text-center text-muted-foreground mb-4">
                    </div>
                  ) : (
                    currentMessages.map((message) => (
                      <Card key={message.query_uuid} className="border-2 border-primary/20 bg-background shadow-md" ref={message.query_uuid === lastMessageId ? lastMessageRef : null}>
                        <div className="pl-0 pr-4 py-2 space-y-3">
                          {/* User Question - aligned to the right */}
                          <div className="flex justify-end pr-4">
                            <div className="max-w-[85%] min-w-[120px] w-fit">
                              <div className="flex items-center gap-2 mb-1 justify-end">
                                {/* <span className="text-xs text-muted-foreground">You</span> */}
                                <span className="text-xs text-muted-foreground">
                                  {formatDate(message.timestamp, {includeTime: true})}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary">
                                  {(() => {
                                    if (typeof message.interaction_type === "undefined") {
                                      console.error("message.interaction_type is undefined; this should not happen.");
                                    }
                                    const mode = Object.values(mode_options).find(
                                      opt => opt.interaction_type === message.interaction_type
                                    );
                                    const Icon = mode ? mode.Icon : MessageSquare;
                                    return (
                                      <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span>
                                        <Icon className="h-3 w-3" />
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{mode.text}</p>
                                      </TooltipContent>
                                      </Tooltip>
                                    );
                                  })()}
                                </div>
                              <div className="p-3 rounded-md bg-primary text-primary-foreground w-fit">
                                  <p className="text-sm whitespace-pre-wrap break-words">{message.query}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* AI Response - aligned to the left */}
                          {message.response && (
                            <div className="flex justify-start pl-4">
                              <div className="max-w-[85%] min-w-[120px] w-fit">
                                <div className="flex items-center gap-2 mb-1">
                                  <div className="flex justify-center w-6 h-6">
                                    <img 
                                      src="/study-buddy.svg" 
                                      alt="Study Buddy" 
                                      className="h-4 w-4"
                                    />
                                  </div>
                                  <span className="text-xs text-muted-foreground">Study Buddy</span>
                                </div>
                                <div className="p-3 rounded-md bg-muted w-fit">
                                  {/* Choose response component based on interaction_type */}
                                  {(() => {
                                    const mode = Object.values(mode_options).find(
                                      opt => opt.interaction_type === message.interaction_type
                                    );
                                    const ResponseComponent = mode?.response_component || ChatResponse;
                                    return (
                                      <SourceProvider>
                                        <ResponseComponent message={message}/>
                                      </SourceProvider>
                                    );
                                  })()}
                                  {/* Feedback and Action Buttons */}
                                  <div className="flex items-center gap-4 mt-3 pt-2 border-t border-border">
                                    <Feedback 
                                      conversation_id={currentConversationId}
                                      message_id={message.query_uuid}
                                      conversation_course={courseLabel}
                                      existingVote={message.vote}
                                      onVoteUpdate={updateMessageById}
                                    />

                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleCopyMessage(message.response)}
                                          className="h-6 px-1.5 text-muted-foreground hover:text-foreground"
                                        >
                                          <Copy className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Copy message</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Loading indicator for pending response */}
                          {!message.response && isWaitingForResponse && message === currentMessages[currentMessages.length - 1] && (
                            <div className="flex justify-start pl-4">
                              <div className="max-w-[85%] min-w-[200px] w-fit">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    Study Buddy
                                    <img 
                                      src="/study-buddy.svg" 
                                      alt="Study Buddy" 
                                      className="h-3 w-3"
                                    />
                                  </span>
                                  <span className="text-xs text-muted-foreground">analyzing your question...</span>
                                </div>
                                <div className="bg-muted p-3 rounded-md">
                                  <div className="flex items-center gap-1">
                                    <div className="flex space-x-1">
                                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
                                    </div>
                                    <span className="text-xs text-muted-foreground ml-2">Processing and searching course materials...</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </Card>
                    ))
                  )}
                </div>


              </ScrollArea>

              
              {/* Chat input with mode selector */}
              <div className="px-3 pt-4 pb-2">
                <div className="w-full max-w-5xl mx-auto">
                  {/* Search option indicators */}
                  {webSearchEnabled && (
                    <div className="mb-2 flex items-center gap-2">
                      <div className="inline-flex items-center bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full text-[10px] font-medium border border-blue-200 dark:border-blue-800">
                        <span>Web</span>
                        <button 
                          onClick={() => setWebSearchEnabled(false)}
                          className="ml-1 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Main input container with ring border */}
                  <div className="relative ring-2 ring-primary/20 focus-within:ring-primary/40 rounded-md">
                    
                    
                    {/* Textarea */}
                    <Textarea
                      ref={textareaRef}
                      dir="auto"
                      placeholder={
                        webSearchEnabled && modeOption !== 'search' 
                          ? mode_options[modeOption].input_placeholder_text.replace('...', ' and search the web...')
                          : mode_options[modeOption].input_placeholder_text
                      }
                      value={input}
                      onChange={(e) => {
                        const textarea = e.target;
                        setInput(e.target.value);
                        
                        // Auto-resize functionality
                        textarea.style.height = 'auto';
                        textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
                      }}
                      onKeyPress={handleKeyPress}
                      disabled={isWaitingForResponse}
                      rows={1}
                      className="w-full border-0 bg-transparent py-2 min-h-[40px] max-h-40 resize-none overflow-y-auto text-base placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 px-3 pr-14"
                      style={{
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'pre-wrap'
                      }}
                    />
                    
                    {/* Send button positioned absolute inside */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          onClick={isWaitingForResponse ? () => {} : handleSendMessage}
                          className="absolute right-2 bottom-2 h-8 w-8 p-0 bg-primary hover:bg-primary/90 text-primary-foreground"
                          disabled={!isWaitingForResponse && !input.trim()}
                        >
                          {isWaitingForResponse ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Submit</p>
                      </TooltipContent>
                    </Tooltip>
                    
                    {/* Mode selector pills at bottom of input */}
                    <div className="px-3 pb-2">
                      <div className="inline-flex items-center bg-muted/50 border rounded-full p-0.5 text-xs">
                        {Object.values(mode_options).map((_mode_option) => (
                          <button
                            key={_mode_option.mode_name}
                            onClick={() => handleSetModeOption(_mode_option.mode_name)}
                            disabled={_mode_option.disabled}
                            className={`px-1.5 py-0.5 text-[10px] rounded-full transition-all duration-200 whitespace-nowrap ${
                              modeOption === _mode_option.mode_name
                                ? 'bg-primary text-primary-foreground'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            <span className="flex items-center gap-1">
                              {_mode_option.text}
                            </span>
                          </button>
                        ))}

                        {/* New conversation plus button */}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={onNewConversation}
                              className="px-1 py-0.5 text-[10px] rounded-full transition-transform duration-200 whitespace-nowrap text-muted-foreground hover:text-foreground group"
                            >
                              <Plus className="h-3 w-3 transition-transform duration-200 group-hover:rotate-45" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>New conversation</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground text-center px-2 mt-2">
                  Study Buddy can make mistakes. Double-check important information.
                </p>
              </div>
              
            </div>

            {/* Right Pane - Lectures & Materials */}
            {!rightPanelCollapsed ? (
              <div 
                className="bg-muted relative"
                style={{ width: `${rightPanelWidth}px` }}
              >
                {/* Resize Handle */}
                <div
                  className="absolute left-0 top-0 w-1 h-full bg-transparent hover:bg-primary/30 cursor-col-resize transition-colors z-10 group"
                  onMouseDown={handleResizeStartRight}
                  onDoubleClick={handleDoubleClickRight}
                  title="Drag to resize panel, double-click to reset"
                >
                  {/* Visual indicator */}
                  <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-primary/0 group-hover:bg-primary/50 rounded-full transition-colors" />
                </div>
                <LecturesMaterialsPanel 
                  course={selectedCourse} 
                  user={user} 
                  onSendTextToChat={handleSendSelectedTextToChat}
                  onCollapse={() => setRightPanelCollapsed(true)}
                />
              </div>
            ) : (
              // Collapsed Right Panel
              <div className="w-32 bg-muted flex flex-col items-center p-4">
                <div className="flex flex-col items-center gap-2">
                  {/* Expand Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRightPanelCollapsed(false)}
                    className="w-full flex flex-col items-center gap-1 h-auto py-3"
                  >
                    <BookOpen className="h-4 w-4" />
                    <span className="text-xs">Lectures & Materials</span>
                  </Button>
                </div>
              </div>
            )}
              
          </div>
        </div>

      </div>
    </TooltipProvider>
  );
};

export default ChatPage;
